import axios from 'axios'
import { handleErrorMsg } from '@/utils/errorHandler'
import { tokenUtil } from '@/utils/'
import router from '@/plugins/router'

let isRefreshing = false
const requestQueue = []

// convention api base path /api/asyncmq:  with zcp
const baseURL = window.__POWERED_BY_QIANKUN__ ? '/api/asyncmq' : process.env.VUE_APP_API_URL
const refreshUrl = baseURL + '/api/v1/auth/token_refresh'
export function genRequestInstance (config) {
  return axios.create({
    baseURL
  })
}

let instance = genRequestInstance()

const nonAuthUrls = ['/token_refresh', '/auth/login']
export class AxiosInterceptors {
  _tokenType
  constructor (tokenType = 'default') {
    this._tokenType = tokenType
  }
  _getToken () {
    return tokenUtil.getToken()
  }
  // can rewrite to handle different header auth
  _setHeaderAuth (config, refresh = false) {
    if (config.headers.Authorization && !refresh) return config
    config.headers['Authorization'] = `Bearer ` + this._getToken()
  }

  _rejectQueue (err) {
    while (requestQueue.length > 0) {
        let { callback } = requestQueue.shift()
        callback(err)
    }
  }
   _processQueue () {
    while (requestQueue.length > 0) {
        const { config, callback } = requestQueue.shift()
        this._setHeaderAuth(config, true)
        instance(config)
        .then(res => callback(null, res))
        .catch(err => callback(err))
    }
  }
  // can rewrite to handle different error responese
  _handleErrorMsg (responese) {
    handleErrorMsg(responese)
  }
  // can rewrite to get different token
  async _refreshToken () {
    // use refreshToken to get token
    try {
      const { data = {} } = await genRequestInstance()({
        method: 'post',
        url: refreshUrl,
        headers: { 'Authorization': `Bearer ` + tokenUtil.getRefreshToken() }
      })
      tokenUtil.set(data?.refreshToken, 'refreshToken')
      tokenUtil.set(data?.token, 'token')
    } catch (error) {
      return Promise.reject(error)
    }
  }
   async _getRefreshToken () {
    // refresh token
    if (isRefreshing) {
      return
    }
    isRefreshing = true
    try {
      if (tokenUtil.validateTokenExp(tokenUtil.getRefreshToken())) {
        await this._refreshToken()
        this._processQueue()
      } else {
        if (!this.timer) {
          this.timer = setTimeout(() => {
            this._handleErrorMsg({ status: 401 })
            tokenUtil.set('', 'token')
            router.replace('/login')
            clearTimeout(this.timer)
          }, 200)
        }
      }
    } catch (refreshErr) {
      // refreshToken expires
      if (refreshErr?.message === '401' || [401, 403].includes(refreshErr.response?.status)) {
        this._handleErrorMsg(refreshErr.response || { status: 401 })
        tokenUtil.set('', 'token')
        router.replace('/login')
        // location.reload()
      }
      this._rejectQueue(refreshErr)
    } finally {
      isRefreshing = false
    }
  }

  requestHandler (config) {
    this._setHeaderAuth(config)
    return config
  }
  requestErrorHandler (error) {
    Promise.reject(error)
  }
  responeseHandler (response) {
    const url = response?.config?.url
    if (url && !nonAuthUrls.filter(u => url.includes(u)).length && !tokenUtil.validateTokenExp(this._getToken())) {
      // refresh token
      this._getRefreshToken()
    }
    if (response?.status !== 200) {
     this._handleErrorMsg(response)
      return Promise.reject(new Error('Unknown Error!'))
    } else {
      return response.data
    }
  }
  async responeseErrorHandler (error) {
    const { response: { status = 0 } = {}, config = {} } = error
    // 401 token expired
    if (status === 401 && config.url && !nonAuthUrls.filter(u => config.url.includes(u)).length) {
      // refresh token
      this._getRefreshToken()
      // the 401 request temporarily suspended with queue
      return new Promise((resolve, reject) => {
        requestQueue.push({
          config: error.config,
          callback (err, data) {
            err ? reject(err) : resolve(data)
          }
        })
      })
    } else {
      this._handleErrorMsg(error.response)
      return Promise.reject(error)
    }
  }
}

function initService (service) {
  const interceptors = new AxiosInterceptors()

  service.interceptors.request.use(
    (config) => {
      return interceptors.requestHandler.bind(interceptors)(config)
    },
    interceptors.requestErrorHandler)

  service.interceptors.response.use(
    (response) => {
      return interceptors.responeseHandler.bind(interceptors)(response)
    },
    (error) => {
      return interceptors.responeseErrorHandler.bind(interceptors)(error)
    }

  )

  // retry interceptors
  // Add the "retryTimes" "retryDelay" configuration to the request config
  service.interceptors.response.use(null, (err) => {
    let config = err.config
    if (!config || !config.retryTimes) return Promise.reject(err)
    const { __retryCount = 0, retryDelay = 300, retryTimes } = config
    // Set the number of retries on the request object
    config.__retryCount = __retryCount
    // Determine whether the number of retries has been exceeded
    if (__retryCount >= retryTimes) {
      return Promise.reject(err)
    }
    // Increase the number of retries
    config.__retryCount++
    // Delay processing
    const delay = new Promise((resolve) => {
      setTimeout(() => {
        resolve()
      }, retryDelay)
    })
    // restart the request
    return delay.then(function () {
      return service(config)
    })
  })
}
initService(instance)

export default instance
